import { AlertCircle, CheckCircle2, Loader2, Mail, XCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../../../ui/components/ui/dialog';
import { Button } from '../../../ui/components/ui/button';
import Preloader from '../../../ui/components/ui/preloader';
import { useEffect, useRef, useState } from 'react';
import { MassMailStreamEvent, OrganizationRecipientResponseDto } from '../types/response';
import { CustomEventSource } from '../lib/custom-event-source';

interface SendProgressProps {
  isOpen: boolean;
  abortDialog: () => void;
  abortAndSetRecipients: (recipients: OrganizationRecipientResponseDto[]) => void;
  requestId: string;
  accessToken: string;
}

export const SendProgress = ({ isOpen, abortDialog, abortAndSetRecipients, requestId, accessToken }: SendProgressProps) => {
  const [isComplete, setIsComplete] = useState(false);

  const handleOpenChange = (open: boolean): void => {
    if (!open && isComplete) {
      abortDialog();
      return;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl w-full max-h-[90vh] p-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b flex-shrink-0">
          <DialogTitle>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Mail className="w-6 h-6 text-blue-600" />
                <h2 className="text-2xl font-bold text-gray-900">E-Mail Versand</h2>
              </div>
            </div>
          </DialogTitle>
        </DialogHeader>
        {isOpen && requestId && accessToken ? (
          <SendProgressDialogContent
            abortDialog={abortDialog}
            requestId={requestId}
            accessToken={accessToken}
            isComplete={isComplete}
            setIsComplete={setIsComplete}
            abortAndSetRecipients={abortAndSetRecipients}
          />
        ) : (
          <Preloader />
        )}
      </DialogContent>
    </Dialog>
  );
};

interface SendProgressDialogContentProps {
  requestId: string;
  accessToken: string;
  isComplete: boolean;
  setIsComplete: (isComplete: boolean) => void;
  abortAndSetRecipients: (recipients: OrganizationRecipientResponseDto[]) => void;
  abortDialog: () => void;
}

const SendProgressDialogContent = ({
  requestId,
  accessToken,
  isComplete,
  setIsComplete,
  abortAndSetRecipients,
  abortDialog
}: SendProgressDialogContentProps) => {
  const [progress, setProgress] = useState<{
    total: number;
    success: number;
    failed: number;
    current: number;
  }>({ total: 0, success: 0, failed: 0, current: 0 });

  const [recipients, setRecipients] = useState<OrganizationRecipientResponseDto[]>([]);
  const [recipientStatus, setRecipientStatus] = useState<Record<string, 'success' | 'error' | 'pending'>>({});
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [hasStarted, setHasStarted] = useState(false);

  const allRecipientsRef = useRef<OrganizationRecipientResponseDto[]>([]);

  useEffect(() => {
    setProgress({ total: 0, success: 0, failed: 0, current: 0 });
    setRecipients([]);
    setRecipientStatus({});
    setErrorMessage(null);
    setIsComplete(false);
    setHasStarted(false);

    const url = `${process.env.REACT_APP_API}/v1.0/mass-mails/internal/send/${requestId}`;

    const eventSource = new CustomEventSource(url, {
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    });

    eventSource.onOpen = () => {
      console.log('✅ [SSE] Connection opened');
    };

    eventSource.onMessage = (event) => {
      console.log('📨 [SSE] Event received:', {
        data: event.data,
        parsed: event.parsed
      });

      setHasStarted(true);

      const streamData: MassMailStreamEvent = event.parsed;

      switch (streamData.type) {
        case 'recipients':
          setRecipients(streamData.data.recipients);
          allRecipientsRef.current = streamData.data.recipients;
          setProgress((prev) => ({
            ...prev,
            total: streamData.data.recipients.length
          }));
          // Initialize all recipients as pending
          streamData.data.recipients.forEach((r) => {
            setRecipientStatus((prev) => ({ ...prev, [r._id]: 'pending' }));
          });
          break;
        case 'progress':
          setProgress((prev) => ({
            ...prev,
            current: prev.current + 1,
            success: streamData.data.status === 'success' ? prev.success + 1 : prev.success,
            failed: streamData.data.status === 'error' ? prev.failed + 1 : prev.failed
          }));
          setRecipientStatus((prev) => ({
            ...prev,
            [streamData.data.recipientId]: streamData.data.status
          }));
          break;
        case 'complete':
          setProgress((prev) => ({
            ...prev,
            total: streamData.data.total,
            success: streamData.data.success,
            failed: streamData.data.failed,
            current: streamData.data.total
          }));
          break;
        case 'error':
          setErrorMessage(streamData.data.message);
          break;
        default:
          break;
      }
    };

    eventSource.onError = (error) => {
      console.error('❌ [SSE] Error:', error);
    };

    eventSource.onClose = () => {
      console.log('✅ [SSE] Connection closed');
      setIsComplete(true);
    };
  }, [requestId, accessToken, setIsComplete]);

  const progressPercentage = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <div className="flex flex-col flex-1 overflow-hidden px-6 py-4">
      {errorMessage && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-md text-red-700 mb-4">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{errorMessage}</span>
        </div>
      )}
      {hasStarted && (
        <div className="flex flex-col flex-1 overflow-hidden space-y-4">
          {/* Progress Bar */}
          <div className="space-y-2 flex-shrink-0">
            <div className="flex justify-between text-sm font-medium">
              <span>
                Fortschritt: {progress.current} / {progress.total}
              </span>
              <span>{Math.round(progressPercentage)}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
              <div
                className="bg-blue-600 h-3 transition-all duration-300 rounded-full flex items-center justify-end pr-2"
                style={{ width: `${progressPercentage}%` }}>
                {progressPercentage > 10 && <span className="text-xs text-white font-medium">{Math.round(progressPercentage)}%</span>}
              </div>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-3 gap-2 flex-shrink-0">
            <div className="flex flex-col items-center p-3 bg-green-50 rounded-lg border border-green-200">
              <CheckCircle2 className="w-5 h-5 text-green-600 mb-1" />
              <div className="text-xl font-bold text-green-700">{progress.success}</div>
              <div className="text-xs text-green-600">Erfolgreich</div>
            </div>
            <div className="flex flex-col items-center p-3 bg-red-50 rounded-lg border border-red-200">
              <XCircle className="w-5 h-5 text-red-600 mb-1" />
              <div className="text-xl font-bold text-red-700">{progress.failed}</div>
              <div className="text-xs text-red-600">Fehler</div>
            </div>
            <div className="flex flex-col items-center p-3 bg-gray-50 rounded-lg border border-gray-200">
              {!isComplete ? <Loader2 className="w-5 h-5 text-gray-600 mb-1 animate-spin" /> : <AlertCircle className="w-5 h-5 text-gray-600 mb-1" />}
              <div className="text-xl font-bold text-gray-700">{progress.total - progress.current}</div>
              <div className="text-xs text-gray-600">Ausstehend</div>
            </div>
          </div>

          {/* Recipients List */}
          {recipients.length > 0 && (
            <div className="flex flex-col flex-1 overflow-hidden space-y-2 min-h-0">
              <h4 className="font-medium text-sm flex-shrink-0">Empfänger Status:</h4>
              <div className="flex-1 overflow-y-auto space-y-1 border rounded-lg p-2 min-h-0">
                {recipients.map((recipient) => {
                  const status = recipientStatus[recipient._id] || 'pending';

                  // Gradient background for success
                  const successGradient = {
                    background: `
                      radial-gradient(circle at 10% 10%, rgba(95, 213, 222, 0.1) 0%, transparent 40%),
                      radial-gradient(circle at 90% 90%, rgba(192, 222, 96, 0.1) 0%, transparent 40%),
                      linear-gradient(135deg, rgba(95, 213, 222, 0.15) 0%, rgba(192, 222, 96, 0.15) 100%)
                    `
                  };

                  // Red background for error
                  const errorGradient = {
                    background: `
                      radial-gradient(circle at 10% 10%, rgba(239, 68, 68, 0.1) 0%, transparent 40%),
                      radial-gradient(circle at 90% 90%, rgba(220, 38, 38, 0.1) 0%, transparent 40%),
                      linear-gradient(135deg, rgba(239, 68, 68, 0.15) 0%, rgba(220, 38, 38, 0.15) 100%)
                    `
                  };

                  const gradientStyle = status === 'success' ? successGradient : status === 'error' ? errorGradient : {};

                  return (
                    <div
                      key={recipient._id}
                      className={`flex items-center justify-between text-xs p-2 rounded transition-colors ${
                        status === 'pending' ? 'bg-gray-50 hover:bg-gray-100' : ''
                      }`}
                      style={gradientStyle}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className="flex-shrink-0">
                          {status === 'success' && <CheckCircle2 className="w-4 h-4 text-green-600" />}
                          {status === 'error' && <XCircle className="w-4 h-4 text-red-600" />}
                          {status === 'pending' && <Loader2 className="w-4 h-4 text-gray-400 animate-spin" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate">
                            {recipient.name} ({recipient.customerNumber})
                          </div>
                          <div className="text-gray-500 truncate">{recipient.adminMail || '-'}</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Completion Summary */}
          {isComplete && !errorMessage && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex-shrink-0">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Versand abgeschlossen</h4>
              </div>
              <div className="text-sm text-blue-700 space-y-1">
                <p>
                  <strong>{progress.success}</strong> von <strong>{progress.total}</strong> E-Mails wurden erfolgreich versendet.
                </p>
                {progress.failed > 0 && (
                  <p className="text-red-600">
                    <strong>{progress.failed}</strong> E-Mails konnten nicht versendet werden.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {!hasStarted && !errorMessage && (
        <div className="flex flex-col items-center justify-center py-8 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin mb-4" />
          <p>Versand wird vorbereitet...</p>
        </div>
      )}
      <DialogFooter className="px-6 py-4 border-t flex-shrink-0">
        <div className="flex gap-2 w-full justify-end">
          {progress.failed > 0 && isComplete && (
            <Button
              variant="outline"
              onClick={() => {
                abortAndSetRecipients(recipients.filter((r) => recipientStatus[r._id] === 'error'));
              }}>
              <XCircle className="w-4 h-4 mr-2" />
              Fehlgeschlagene Empfänger übernehmen ({progress.failed})
            </Button>
          )}
          <Button onClick={abortDialog} disabled={!isComplete}>
            Schließen
          </Button>
        </div>
      </DialogFooter>
    </div>
  );
};
